// Recurring bookings Phase 3 — bulk series actions (edit all/following, cancel following/all, end).
// One modal for every bulk scope: shows the COUNTED impact, the step-up password (bulk ⇒ re-auth),
// and for edit the template fields prefilled from the launching occurrence. Single-occurrence
// edit/cancel + postpone stay on the existing booking flows (no step-up).

import { useState } from 'react';
import { apiFetch } from '../../lib/api';
import { C, F } from './theme';
import { ModalOverlay, Field, inputStyle } from './atoms';
import type { Base, Booking, BookingKind, CompanySettings, ReasonCode, TrainingActivity } from '../../lib/opsBoardTypes';
import { DEFAULT_DURATION_MINS, durationOptions, formatDurationLabel, initialDurationMins } from '../../lib/ops-board/duration';

export type SeriesAction =
  | { kind: 'edit'; scope: 'all' | 'following' }
  | { kind: 'cancel'; scope: 'all' | 'following' }
  | { kind: 'end' };

interface Props {
  booking: Booking;        // the occurrence the action launched from (its series_id + slot date)
  action: SeriesAction;
  impact: number;          // counted future occurrences this will affect (computed by the caller)
  bases: Base[];
  activities: TrainingActivity[];
  bookingKinds: BookingKind[];
  reasonCodes: ReasonCode[];
  // The tenant's standard booking length reaches the template's duration seed through here.
  // ⚠ NULLABLE — offline-cached singleton (offline/prefetch.ts); never assume it is present.
  settings: CompanySettings | null;
  onClose: () => void;
  onDone: (message: string) => void;
}

export default function SeriesActionModal({ booking, action, impact, bases, activities, bookingKinds, reasonCodes, settings, onClose, onDone }: Props) {
  const seriesId = booking.series_id!;
  const fromDate = booking.series_occurrence_date ?? undefined; // boundary for "following"
  const cancelReasons = reasonCodes.filter((r) => r.context === 'cancellation');

  // Edit template fields, prefilled from the launching occurrence.
  const [baseId, setBaseId] = useState(booking.base_id);
  const [activityId, setActivityId] = useState(booking.activity_id ?? '');
  const [kindId, setKindId] = useState(booking.booking_kind_id ?? '');
  const [title, setTitle] = useState(booking.title ?? '');
  // PR-000057 — the occurrence's REAL length in minutes, verbatim. The option list is an input
  // convenience; applying it as a whitelist here truncated every off-grid recurring booking.
  const [durationMins, setDurationMins] = useState(() =>
    initialDurationMins(booking.activity_mins, settings?.default_booking_mins ?? DEFAULT_DURATION_MINS));
  const [notes, setNotes] = useState(booking.activity_note ?? '');
  // Carried for the same reason `notes` is: an all/following edit DELETES the future
  // un-overridden occurrences and regenerates them from this template, so a field omitted here
  // is silently lost on every regenerated occurrence.
  const [internalNote, setInternalNote] = useState(booking.internal_note ?? '');
  const [maxParticipants, setMaxParticipants] = useState(booking.max_participants != null ? String(booking.max_participants) : '');
  const [hardLimit, setHardLimit] = useState(booking.participants_limit_hard);

  const [cancelReasonId, setCancelReasonId] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scope = action.kind === 'end' ? 'all' : action.scope;
  const scopeLabel = action.kind === 'end' ? '' : scope === 'all' ? 'all in the series' : 'this and all following';
  const title2 =
    action.kind === 'edit' ? `Edit recurring series — ${scopeLabel}`
    : action.kind === 'cancel' ? `Cancel recurring — ${scopeLabel}`
    : 'Stop recurring';

  async function submit() {
    setError(null);
    if (!password.trim()) { setError('Re-enter your password to confirm.'); return; }
    if (action.kind === 'cancel' && !cancelReasonId) { setError('Choose a cancellation reason.'); return; }
    setBusy(true);
    try {
      const step_up = { password };
      let res;
      if (action.kind === 'edit') {
        const template: Record<string, unknown> = {
          base_id: baseId,
          activity_mins: durationMins,
          participants_limit_hard: hardLimit,
        };
        if (activityId) template.activity_id = activityId;
        if (kindId) template.booking_kind_id = kindId;
        if (title.trim()) template.title = title.trim();
        if (maxParticipants.trim()) template.max_participants = Number(maxParticipants);
        if (notes.trim()) template.notes = notes;
        if (internalNote.trim()) template.internal_note = internalNote;
        res = action.scope === 'all'
          ? await apiFetch<{ replaced: number; created: number }>(`/api/booking-series/${seriesId}`, { method: 'PATCH', body: JSON.stringify({ template, step_up }) })
          : await apiFetch<{ replaced: number; created: number }>(`/api/booking-series/${seriesId}/split`, { method: 'POST', body: JSON.stringify({ from_occurrence_date: fromDate, template, step_up }) });
        if (res.error) { handleErr(res.error); return; }
        onDone(`Updated the series — ${res.data?.created ?? 0} future booking(s) regenerated.`);
      } else if (action.kind === 'cancel') {
        res = await apiFetch<{ cancelled: number }>(`/api/booking-series/${seriesId}/cancel`, {
          method: 'POST',
          body: JSON.stringify({ scope: action.scope, from_occurrence_date: action.scope === 'following' ? fromDate : undefined, cancel_reason_id: cancelReasonId, cancel_reason: cancelNote.trim() || undefined, step_up }),
        });
        if (res.error) { handleErr(res.error); return; }
        onDone(`Cancelled ${res.data?.cancelled ?? 0} recurring booking(s).`);
      } else {
        res = await apiFetch<{ removed: number }>(`/api/booking-series/${seriesId}/end`, { method: 'POST', body: JSON.stringify({ step_up }) });
        if (res.error) { handleErr(res.error); return; }
        onDone(`Stopped recurring — removed ${res.data?.removed ?? 0} future unassigned booking(s).`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setBusy(false);
    }
  }

  function handleErr(err: { code?: string; message: string }) {
    if (err.code === 'STEP_UP_FAILED') setError('Password is incorrect.');
    else if (err.code === 'STEP_UP_REQUIRED') setError('Re-enter your password to confirm.');
    else setError(err.message);
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto', fontFamily: F }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>{title2}</div>
        <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 14 }}>This is part of a recurring booking.</div>

        {/* Counted impact */}
        <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#4f46e5' }}>
          {action.kind === 'edit' && `This will replace ${impact} future unassigned booking${impact === 1 ? '' : 's'} (assigned/edited days are left as they are).`}
          {action.kind === 'cancel' && `This will cancel ${impact} future booking${impact === 1 ? '' : 's'}.`}
          {action.kind === 'end' && `This will remove ${impact} future unassigned booking${impact === 1 ? '' : 's'}; assigned days are left standing.`}
        </div>

        {action.kind === 'edit' && (
          <>
            <Field label="Base">
              <select value={baseId} onChange={(e) => setBaseId(e.target.value)} style={inputStyle}>
                {bases.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.short_code})</option>)}
              </select>
            </Field>
            <Field label="Title (optional)"><input value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} style={inputStyle} /></Field>
            <Field label="Activity">
              <select value={activityId} onChange={(e) => setActivityId(e.target.value)} style={inputStyle}>
                <option value="">—</option>
                {activities.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Field>
            <Field label="Booking Kind">
              <select value={kindId} onChange={(e) => setKindId(e.target.value)} style={inputStyle}>
                <option value="">—</option>
                {bookingKinds.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </Field>
            <Field label="Duration">
              <select value={durationMins} onChange={(e) => setDurationMins(Number(e.target.value))} style={inputStyle}>
                {durationOptions(durationMins).map((m) => (
                  <option key={m} value={m}>{formatDurationLabel(m)}</option>
                ))}
              </select>
            </Field>
            <Field label="Notes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: 48, resize: 'vertical' }} /></Field>
            <Field label="Internal note (staff only)"><textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} placeholder="Not shown to students." style={{ ...inputStyle, minHeight: 48, resize: 'vertical' }} /></Field>
          </>
        )}

        {action.kind === 'cancel' && (
          <>
            <Field label="Cancellation reason">
              <select value={cancelReasonId} onChange={(e) => setCancelReasonId(e.target.value)} style={inputStyle}>
                <option value="">— select a reason —</option>
                {cancelReasons.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </Field>
            <Field label="Note (optional)"><textarea value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} style={{ ...inputStyle, minHeight: 40, resize: 'vertical' }} /></Field>
          </>
        )}

        {/* Step-up — bulk actions re-verify the actor's password server-side. */}
        <Field label="Re-enter your password to confirm">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" style={inputStyle} />
        </Field>

        {error && <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 6, padding: '8px 10px', fontSize: 11, color: C.red, marginBottom: 8 }}>⚠ {error}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <button onClick={onClose} disabled={busy} style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: 6, padding: '9px', fontSize: 12, cursor: 'pointer' }}>Back</button>
          <button onClick={() => void submit()} disabled={busy} style={{ flex: 2, background: action.kind === 'edit' ? C.navy : C.red, border: 'none', color: '#fff', borderRadius: 6, padding: '9px', fontSize: 12, fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}>
            {busy ? 'Working…' : action.kind === 'edit' ? 'Confirm changes' : action.kind === 'cancel' ? 'Confirm cancel' : 'Stop recurring'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
